import { Link } from 'react-router-dom'
import { Avatar } from '@/components/Avatar'
import type { SearchHitResponse } from '@/api/types'
import { Icon } from '@/components/Icon'
import { formatIdentityLine } from '@/lib/age'
import { formatLastVisit } from '@/lib/dates'
import { RESULT_ROW, RESULT_ROW_CHEVRON } from '@/components/ResultRow'

/** Exactly enough to pick the right person and not one field more — a result
 *  row is not the place to ship an address across the wire. */
export function PatientResultRow({
  hit,
  onSelect,
}: {
  hit: SearchHitResponse
  onSelect?: () => void
}) {
  const identity = formatIdentityLine(hit.age, hit.sex)
  return (
    <li>
      {/* A list row, not a card: every row that does not fit is another scroll
          mid-consultation. */}
      <Link
        to={`/patients/${hit.id}`}
        className={RESULT_ROW}
        onClick={onSelect}
        aria-label={`${hit.full_name}, ${identity || 'details not recorded'}, OP number ${hit.op_number}`}
      >
        {/* An anchor for the eye: twenty rows of nothing but text is a wall to
            scan. */}
        <Avatar name={hit.full_name} size={36} />
        <span className="flex min-w-0 flex-1 flex-col gap-px">
          {/* On a phone the name and the OP number cannot both fit on one line,
              and the name is the thing being read. */}
          <span className="flex min-w-0 items-baseline gap-2 max-[30rem]:flex-wrap max-[30rem]:gap-y-0">
            <span className="truncate text-base leading-tight font-semibold">{hit.full_name}</span>
            <span className="text-xs whitespace-nowrap text-fg-subtle tabular-nums">
              {hit.op_number}
            </span>
          </span>
          <span className="truncate text-xs leading-snug text-fg-muted">{identity}</span>
        </span>
        <span className="text-right text-xs whitespace-nowrap text-fg-muted tabular-nums">
          {formatLastVisit(hit.last_visit_at)}
        </span>
        <span className={RESULT_ROW_CHEVRON}>
          <Icon name="chevronRight" size="1rem" />
        </span>
      </Link>
    </li>
  )
}
